import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import type { ManualEntryCategory } from './manualEntryTypes';
import { parseCategoryBatchInput } from './manualEntryUtils';

interface BatchImportModalProps {
  open: boolean;
  onClose: () => void;
  onImport: (categories: ManualEntryCategory[]) => void;
  existingCount: number;
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(17, 24, 39, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 40,
};

const modalStyle: CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  width: 'min(720px, 90vw)',
  maxHeight: '85vh',
  padding: '24px',
  boxShadow: '0 20px 45px rgba(0,0,0,0.16)',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

export function BatchImportModal({ open, onClose, onImport, existingCount }: BatchImportModalProps) {
  const [rawInput, setRawInput] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) {
      setRawInput('');
      setTouched(false);
    }
  }, [open]);

  const parseResult = useMemo(() => parseCategoryBatchInput(rawInput), [rawInput]);
  const { categories, errors, format } = parseResult;

  const canImport = categories.length > 0 && errors.length === 0;

  const previewCategories = categories.slice(0, 5);

  const handleImport = () => {
    setTouched(true);
    if (!canImport) return;
    onImport(categories);
    onClose();
  };

  if (!open) return null;

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 600, margin: 0 }}>Batch Import Categories</h2>
            <p style={{ margin: '4px 0 0', color: '#4b5563', fontSize: '13px' }}>
              Paste CSV (columns: name, display_name, bisac_code, order) or JSON. Existing categories: {existingCount}.
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#6b7280'
            }}
            aria-label="Close batch import"
          >
            x
          </button>
        </header>

        <textarea
          value={rawInput}
          onChange={(e) => setRawInput(e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder={`Example CSV:\nname,display_name,bisac_code,order\nfiction-general,General Fiction,FIC000000,1`}
          style={{
            width: '100%',
            minHeight: '200px',
            borderRadius: '8px',
            border: '1px solid #d1d5db',
            padding: '12px',
            fontSize: '13px',
            fontFamily: 'monospace',
            resize: 'vertical'
          }}
        />

        {touched && errors.length > 0 && (
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px',
            color: '#b91c1c',
            fontSize: '13px'
          }}>
            <strong style={{ display: 'block', marginBottom: '4px' }}>Import issues</strong>
            {errors.map((error) => (
              <div key={error}>• {error}</div>
            ))}
          </div>
        )}

        {categories.length > 0 && (
          <div style={{
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '12px',
            fontSize: '13px'
          }}>
            <div style={{ marginBottom: '8px', color: '#374151' }}>
              Parsed {categories.length} categories from {format.toUpperCase()} input. Showing first {previewCategories.length}:
            </div>
            <ul style={{ margin: 0, paddingLeft: '18px', maxHeight: '160px', overflowY: 'auto' }}>
              {previewCategories.map((category, index) => (
                <li key={`${category.name}-${index}`} style={{ padding: '2px 0' }}>
                  <span style={{ fontWeight: 600 }}>{category.display_name}</span>
                  <span style={{ color: '#6b7280' }}> ({category.name})</span>
                  {category.bisac_code && <span style={{ color: '#6b7280' }}> • BISAC {category.bisac_code}</span>}
                  {typeof category.order === 'number' && <span style={{ color: '#6b7280' }}> • Order {category.order}</span>}
                </li>
              ))}
              {categories.length > previewCategories.length && (
                <li style={{ color: '#6b7280', padding: '2px 0' }}>
                  ...and {categories.length - previewCategories.length} more
                </li>
              )}
            </ul>
          </div>
        )}

        <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={!canImport}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: '#1d4ed8',
              color: 'white',
              cursor: canImport ? 'pointer' : 'not-allowed',
              opacity: canImport ? 1 : 0.5
            }}
          >
            Import
          </button>
        </footer>
      </div>
    </div>
  );
}
